import { createResource } from 'solid-js';
import { print } from 'graphql';
import { asAccessor } from '@solid-primitives/utils';

// src/index.ts
var GraphQLError = class extends Error {
  constructor(message, locations, extensions) {
    super(message);
    this.locations = locations;
    this.extensions = extensions;
  }
};
var createGraphQLClient = (url, options = {}) => (query, variables = {}, resourceOptions) => {
  const getUrl = asAccessor(url), getVariables = asAccessor(variables), getOptions = asAccessor(options);
  return createResource(
    () => {
      const url2 = getUrl(), variables2 = getVariables(), options2 = getOptions();
      return url2 && variables2 && { url: url2, options: { ...options2, variables: variables2 } };
    },
    ({ url: url2, options: options2 }) => request(url2, query, options2),
    resourceOptions
  );
};
async function request(url, query, options = {}) {
  const { fetcher = fetch, variables = {}, method = "POST" } = options;
  const query_string = typeof query == "string" ? query : print(query);
  const res = await fetcher(url, {
    ...options,
    method,
    body: JSON.stringify({ query: query_string, variables }),
    headers: {
      "content-type": "application/json",
      ...options.headers
    }
  });
  const data = await res.json();
  if (data.errors && data.errors.length) {
    throw new GraphQLError(
      data.errors[0].message,
      data.errors[0].locations,
      data.errors[0].extensions
    );
  }
  return data.data;
}
async function multipartRequest(url, query, options = {}) {
  const { fetcher = fetch, variables = {} } = options;
  const query_string = typeof query == "string" ? query : print(query);
  const res = await fetcher(url, {
    ...options,
    method: "POST",
    body: makeMultipartBody(query_string, variables),
    headers: {
      "content-type": "multipart/form-data",
      ...options.headers
    }
  });
  const data = await res.json();
  if (data.errors && data.errors.length) {
    throw new GraphQLError(
      data.errors[0].message,
      data.errors[0].locations,
      data.errors[0].extensions
    );
  }
  return data.data;
}
function makeMultipartBody(query, variables) {
  const parts = [];
  function copyValue(r, k, v, path) {
    if (v instanceof Blob) {
      parts.push({
        blob: v,
        path: path.join(".") + "." + k
      });
      r[k] = null;
    } else {
      if (typeof v === "object" && v != null) {
        path.push(k);
        r[k] = copyObject(v, path);
        path.pop();
      } else {
        r[k] = v;
      }
    }
  }
  function copyObject(obj, path) {
    const r = obj.constructor();
    for (const [k, v] of Object.entries(obj)) {
      copyValue(r, k, v, path);
    }
    return r;
  }
  variables = copyObject(variables, ["variables"]);
  const formData = new FormData();
  formData.append("operations", JSON.stringify({ query, variables }));
  formData.append("map", JSON.stringify(Object.fromEntries(parts.map((x, i) => [`${i}`, x.path]))));
  for (let i = 0; i < parts.length; i++) {
    formData.append(`${i}`, parts[i].blob);
  }
  return formData;
}
var gql = (query, ...expressions) => query.map((s, i) => `${s}${expressions[i] ?? ""}`).join("").replace(/#.+\r?\n|\r/g, "").replace(/\r?\n|\r/g, "").replace(/\s{2,}/g, " ").trim();

export { GraphQLError, createGraphQLClient, gql, makeMultipartBody, multipartRequest, request };
